"use client";

import { useCallback, useId, useLayoutEffect, useRef, useState } from "react";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import { Input } from "~/components/ui/input";
import type { useGameAnalysisAuthorization } from "./useGameAnalysisAuthorization";
import { checkPgnImportSourceBytes, parsePgnWithinImportBudgets } from "./pgnImportBudgets";
import { formatImportError, formatSkippedAnalysisGame, importOutcomeUncertainMessage, readImportAcknowledgement, readImportRejection } from "./importFeedback";
import type { ImportReport } from "./contracts";

// Called by the mounted list host, above its hidden/loading/access branches.
export function useGameAnalysisImportDraft(
  selected: boolean,
  access: ReturnType<typeof useGameAnalysisAuthorization>,
) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"text" | "file">("text");
  const [text, setText] = useState("");
  const [file, setFile] = useState<File>();
  const [report, setReport] = useState<ImportReport>();
  const [uncertain, setUncertain] = useState(false);
  const confirmedOwner = access.status === "forbidden" ? null : access.identityOwnerId;
  const [draftOwner, setDraftOwner] = useState(confirmedOwner);
  if (confirmedOwner !== undefined && draftOwner !== confirmedOwner) {
    setDraftOwner(confirmedOwner);
    setMode("text");
    setText("");
    setFile(undefined);
    setReport(undefined);
    setUncertain(false);
  }
  const [wasSelected, setWasSelected] = useState(selected);
  const [generation, setGeneration] = useState(access.generation);
  if (wasSelected !== selected || generation !== access.generation) {
    setWasSelected(selected);
    setGeneration(access.generation);
    setOpen(false);
    setReport(undefined);
  }
  return { open, setOpen, mode, setMode, text, setText, file, setFile, report, setReport, uncertain, setUncertain, access };
}

export function GameAnalysisImportDialog({ draft, onInspectList, onImported, onWriteStarted }: {
  draft: ReturnType<typeof useGameAnalysisImportDraft>;
  onInspectList: () => void;
  onImported: () => void;
  onWriteStarted: () => () => void;
}) {
  const { open, setOpen, mode, setMode, text, setText, file, setFile, report, setReport, uncertain, setUncertain, access } = draft;
  const textId = useId();
  const fileId = useId();
  const errorId = useId();
  const [error, setError] = useState<string>();
  const [reading, setReading] = useState(false);
  const [pending, setPending] = useState(false);
  const requestPending = useRef(false);
  const activeRequest = useRef<AbortController>();
  const requestTimer = useRef<number>();
  const activeReader = useRef<FileReader>();
  const releaseWrite = useRef<() => void>();
  const abortRead = useCallback(() => {
    const previous = activeReader.current;
    activeReader.current = undefined;
    previous?.abort();
  }, []);
  useLayoutEffect(() => () => {
    abortRead();
    activeRequest.current?.abort();
    activeRequest.current = undefined;
    requestPending.current = false;
    window.clearTimeout(requestTimer.current);
    releaseWrite.current?.();
    releaseWrite.current = undefined;
  }, [abortRead]);
  const cancelRead = () => {
    abortRead();
    setReading(false);
  };
  const submitPgn = (pgn: string) => {
    if (!access.isCurrent() || requestPending.current) return;
    let parsed: ReturnType<typeof parsePgnWithinImportBudgets>;
    try {
      parsed = parsePgnWithinImportBudgets(pgn);
    } catch {
      setError(formatImportError("import-failed"));
      return;
    }
    if (!parsed.ok) {
      setError(parsed.error.message);
      return;
    }
    setError(undefined);
    requestPending.current = true;
    releaseWrite.current = onWriteStarted();
    setReport(undefined);
    setUncertain(false);
    setPending(true);
    const controller = new AbortController();
    activeRequest.current = controller;
    let timeZone: string | undefined;
    try { timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined; } catch { /* Timezone context is optional. */ }
    const body = JSON.stringify({ pgn, timeZone });
    const deadline = performance.now() + 30000;
    const current = () => activeRequest.current === controller && access.isCurrent();
    const finish = () => {
      activeRequest.current = undefined;
      requestPending.current = false;
      window.clearTimeout(requestTimer.current);
      setPending(false);
      releaseWrite.current?.();
      releaseWrite.current = undefined;
    };
    const expire = () => {
      if (!current()) return;
      controller.abort();
      finish();
      setUncertain(true);
    };
    const withinDeadline = () => {
      if (!current()) return false;
      if (performance.now() >= deadline) { expire(); return false; }
      return true;
    };
    requestTimer.current = window.setTimeout(expire, 30000);
    void fetch("/api/game-analysis/import", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      signal: controller.signal,
    }).then(async (response) => {
      if (!withinDeadline()) return;
      if (response.status === 401) { access.refreshSession(); return; }
      if (response.status === 403) { access.invalidate(); return; }
      const result: unknown = await response.json();
      if (!withinDeadline()) return;
      const acknowledgement = readImportAcknowledgement(result, response.status, parsed.originalCount);
      if (acknowledgement) {
        if (!withinDeadline()) return;
        finish();
        setReport(acknowledgement);
        if (acknowledgement.importedCount > 0) {
          setText("");
          setFile(undefined);
          setMode("text");
          setOpen(false);
          onImported();
        }
      } else {
        const rejection = readImportRejection(response.status, result);
        if (!withinDeadline()) return;
        finish();
        if (rejection) setError(rejection);
        else setUncertain(true);
      }
    }).catch(() => {
      if (!withinDeadline()) return;
      finish();
      setUncertain(true);
    });
  };
  const submitFile = (selectedFile: File) => {
    if (!access.isCurrent()) return;
    cancelRead();
    const sizeError = checkPgnImportSourceBytes(selectedFile.size);
    if (sizeError) {
      setError(sizeError.message);
      return;
    }
    setError(undefined);
    setReading(true);
    const reader = new FileReader();
    activeReader.current = reader;
    const current = () => activeReader.current === reader && access.isCurrent();
    reader.onerror = () => {
      if (!current()) return;
      activeReader.current = undefined;
      setReading(false);
      setError("Не удалось прочитать файл PGN");
    };
    reader.onload = () => {
      if (!current()) return;
      activeReader.current = undefined;
      setReading(false);
      if (reader.result instanceof ArrayBuffer) {
        let pgn: string;
        try {
          pgn = new TextDecoder("utf-8", { fatal: true }).decode(reader.result);
        } catch {
          setError("Файл PGN должен быть в кодировке UTF-8");
          return;
        }
        submitPgn(pgn);
      }
    };
    reader.readAsArrayBuffer(selectedFile);
  };
  return <><Dialog open={open} onOpenChange={(nextOpen) => {
    if (!nextOpen && requestPending.current) return;
    if (!nextOpen) cancelRead();
    setOpen(nextOpen);
  }}>
    <DialogTrigger asChild><Button type="button">Импорт PGN</Button></DialogTrigger>
    <DialogContent showCloseButton={false} className="flex h-[80dvh] w-[80vw] max-w-none flex-col overflow-y-auto"
      onEscapeKeyDown={(event) => { if (requestPending.current) event.preventDefault(); }}
      onInteractOutside={(event) => { if (requestPending.current) event.preventDefault(); }}>
      <DialogTitle>Импорт PGN</DialogTitle>
      <DialogDescription>Добавьте одну или несколько партий из PGN.</DialogDescription>
      <fieldset className="flex gap-4" disabled={pending}>
        <legend className="sr-only">Источник PGN</legend>
        <label className="flex items-center gap-2">
          <input type="radio" name={textId} checked={mode === "text"}
            onChange={() => { cancelRead(); setMode("text"); setError(undefined); }} />
          Текст PGN
        </label>
        <label className="flex items-center gap-2">
          <input type="radio" name={textId} checked={mode === "file"}
            onChange={() => { cancelRead(); setMode("file"); setError(undefined); }} />
          Файл PGN
        </label>
      </fieldset>
      {mode === "text" ? <>
        <Label htmlFor={textId}>Текст PGN</Label>
        <Textarea id={textId} value={text} disabled={pending} className="min-h-0 flex-1 resize-none"
          onChange={(event) => setText(event.target.value)}
          aria-invalid={Boolean(error)} aria-describedby={error ? errorId : undefined} />
      </> : <>
        <Label htmlFor={fileId}>Файл PGN</Label>
        <Input id={fileId} type="file" accept=".pgn" disabled={pending}
          onChange={(event) => { cancelRead(); setError(undefined); setFile(event.target.files?.[0]); }}
          aria-invalid={Boolean(error)} aria-describedby={error ? errorId : undefined} />
        {file && <p>Выбран файл: {file.name}</p>}
      </>}
      {report && <ImportReportFeedback report={report} />}
      {error && <p id={errorId} role="alert">{error}</p>}
      {uncertain && <p role="alert">{importOutcomeUncertainMessage}</p>}
      {uncertain && <Button type="button" onClick={() => {
        if (!access.isCurrent() || requestPending.current) return;
        setOpen(false);
        onInspectList();
      }}>Обновить список</Button>}
      {reading && <p role="status">Чтение файла…</p>}
      {pending && <p role="status">Импорт партий…</p>}
      <DialogFooter className="mt-auto shrink-0">
        <DialogClose asChild><Button type="button" variant="outline" disabled={pending}>Отмена</Button></DialogClose>
        <Button type="button" disabled={pending} onClick={() => {
          if (mode === "text" && !text.trim()) setError("Вставьте текст PGN");
          else if (mode === "text") submitPgn(text);
          if (mode === "file" && !file) setError("Выберите файл PGN");
          else if (mode === "file" && file) submitFile(file);
        }}>Импортировать</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
    {!open && report && <ImportReportFeedback report={report} />}
  </>;
}

const openingWarningReasons = {
  "invalid-fen": "некорректный FEN",
  "invalid-pgn": "некорректный PGN",
  "illegal-move": "недопустимый ход",
};

function ImportReportFeedback({ report }: { report: ImportReport }) {
  return <div>
    <p role="status">Импортировано партий: {report.importedCount}. Пропущено: {report.skippedGames.length}.</p>
    {report.skippedGames.length > 0 && <ul>{report.skippedGames.map((game) =>
      <li key={game.gameIndex}>{formatSkippedAnalysisGame(game)}</li>)}</ul>}
    {report.openingWarnings.length > 0 && <>
      <p>Не использовано дебютов: {report.openingWarnings.length}.</p>
      <ul>{report.openingWarnings.map((warning) =>
        <li key={warning.openingId}>Дебют №{warning.openingId} не использован при анализе: {openingWarningReasons[warning.code]}.</li>)}</ul>
    </>}
  </div>;
}
